import {
  ADD_TYPE,
  AND_JOIN_TYPE,
  DaysOfTheWeek,
  EMAIL_MESSAGE_INITIAL_VALUE,
  FlowTypes,
  INFINITY_TEST_DEFAULTS,
  OptimizationMetricOptions,
  OptimizedABSplitTestStatuses,
  RCS_CARD_URL_INITIAL_VALUE,
  RCS_MESSAGE_INITIAL_VALUE,
  RichMessagePanels,
  SMS_MESSAGE_INITIAL_VALUE,
  WaitActionCategories,
} from 'components/flowBuilder/constants';
import { StaticActionsTypes } from 'components/flowBuilder/constants/actions';
import { TextToBuyDrawerTypes } from 'components/flowBuilder/constants/drawers';
import { StaticStepsTypes } from 'components/flowBuilder/constants/steps';
import {
  BinaryCondition,
  Condition,
  ConditionCase,
  ConditionGroup,
  FlowType,
  SubscriberGroup,
  TextToBuyProduct,
  WaitActionCategory,
} from 'components/flowBuilder/types';
import {
  ABSplitAction,
  Action,
  AIManagedMessageAction,
  AIManagedOutboundAction,
  DynamicAction,
  DynamicActionType,
  EndAction,
  OptimizedABSplitAction,
  RandomWaitAction,
  RelativeWeekWaitAction,
  RichMessageAction,
  RichMessageActionParams,
  RichMessageFallback,
  SendEmailAction,
  SendMessageAction,
  SpecificDateTimeWaitAction,
  SubscriberAttributeSplitAction,
  TextToBuyAction,
  TimeDeltaWaitAction,
  TriggerEventSplitAction,
  UpdateSubscriberAction,
  WaitAction,
  WaitForEventSplitAction,
} from 'components/flowBuilder/types/actions';
import {
  isABSplitAction,
  isABSplitActionParams,
  isAIManagedMessageAction,
  isAIManagedMessageActionParams,
  isAIManagedOutboundAction,
  isAIManagedOutboundActionParams,
  isDynamicAction,
  isDynamicActionParams,
  isDynamicSplitAction,
  isOptimizedABSplitAction,
  isOptimizedABSplitActionBackendConfig,
  isOptimizedABSplitActionParams,
  isRichMessageAction,
  isRichMessageActionFrontendConfig,
  isRichMessageActionParams,
  isSendEmailAction,
  isSendEmailActionParams,
  isSendMessageAction,
  isSendMessageActionParams,
  isSplitAction,
  isSubscriberAttributeSplitAction,
  isSubscriberAttributeSplitActionParams,
  isTextToBuyAction,
  isTextToBuyActionParams,
  isTriggerEventSplitAction,
  isTriggerEventSplitActionParams,
  isUpdateSubscriberAction,
  isUpdateSubscriberActionParams,
  isWaitAction,
  isWaitActionParams,
  isWaitForEventSplitAction,
  isWaitForEventSplitActionParams,
} from 'components/flowBuilder/types/actions/typeGuards';
import { TextToBuyDrawerType } from 'components/flowBuilder/types/drawers';
import {
  AutomationTriggerStep,
  CampaignTriggerStep,
} from 'components/flowBuilder/types/steps';
import { isConditionCase } from 'components/flowBuilder/types/typeGuards';
import { Channels } from 'constants/constants';
import { RcsMessageCategories } from 'constants/rcs';
import { cloneDeep } from 'lodash';
import { RcsMessage, RcsMessageCard } from 'types/rcs';
import { v4 as uuidv4 } from 'uuid';
import { getDuplicatedActionCustomTitle } from './actionTitle';
import { getDynamicActionSplitField } from './dynamicActions';

export const createAutomationTriggerStep = (): AutomationTriggerStep => ({
  guid: uuidv4(),
  type: StaticStepsTypes.AUTOMATION_TRIGGER,
  next: [],
  params: {
    triggerEvents: [],
    cancelEvents: [],
    userFilterCriteria: [],
    triggerFilters: [],
    templatedUserFilterCriteria: [],
    transactional: false,
  },
});

export const createCampaignTriggerStep = (): CampaignTriggerStep => ({
  guid: uuidv4(),
  type: StaticStepsTypes.CAMPAIGN_TRIGGER,
  next: [],
  params: {
    safeSend: false,
    requiredConsents: [],
    segmentIds: [],
    excludeSegmentIds: [],
    cancelEvents: [],
  },
});

export const createEmptyTextToBuyProduct = (): TextToBuyProduct => {
  return {
    shopify_product_id: null,
    shopify_product_name: null,
    shopify_variant_id: null,
    shopify_variant_name: null,
    price: null,
    min_price: null,
    max_price: null,
    quantity: 1,
    guid: uuidv4(),
  };
};

export const createTextToBuyAction = (): TextToBuyAction => ({
  guid: uuidv4(),
  type: StaticStepsTypes.TEXT_TO_BUY,
  next: [],
  params: {
    cases: [
      {
        action_guid: '',
        event: 'PayOrderConfirmed',
      },
      {
        action_guid: '',
        event: 'PayOrderCancelled',
      },
      {
        action_guid: '',
        event: 'PayOrderExpired',
      },
      {
        action_guid: '',
        event: 'FlowScheduledEvent',
      },
    ],
    content: SMS_MESSAGE_INITIAL_VALUE,
    mediaUrl: null,
    products: [createEmptyTextToBuyProduct()],
  },
});

export const createTimeDeltaWaitAction = (): TimeDeltaWaitAction => {
  return {
    guid: uuidv4(),
    type: StaticActionsTypes.WAIT,
    params: {
      category: WaitActionCategories.TIMEDELTA,
      duration: '',
    },
    next: [],
  };
};

export const createSpecificDateTimeWaitAction =
  (): SpecificDateTimeWaitAction => {
    return {
      guid: uuidv4(),
      type: StaticActionsTypes.WAIT,
      params: {
        category: WaitActionCategories.SPECIFIC_DATETIME,
        date: '',
      },
      next: [],
    };
  };

export const createRelativeWeekWaitAction = (): RelativeWeekWaitAction => {
  return {
    guid: uuidv4(),
    type: StaticActionsTypes.WAIT,
    params: {
      category: WaitActionCategories.RELATIVE_WEEK,
      relativeDate: DaysOfTheWeek.MONDAY,
      time: '',
    },
    next: [],
  };
};

export const createRandomWaitAction = (): RandomWaitAction => {
  return {
    guid: uuidv4(),
    type: StaticActionsTypes.WAIT,
    params: {
      category: WaitActionCategories.RANDOM,
      minDuration: '',
      maxDuration: '',
    },
    next: [],
  };
};

const createWaitAction = (): WaitAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.WAIT,
  params: {
    category: null,
    useSubscriberTz: false,
    clientTzOffset: '+00:00',
  },
  next: [],
});

export const createEmptyBinaryCondition = (): BinaryCondition => {
  return {
    operator: '',
    type: 'Binary',
    value: '',
    variable: '',
    allow_approximate_matches: false,
  };
};

export const createConditionGroup = (): ConditionGroup => {
  return {
    type: AND_JOIN_TYPE,
    value: [createEmptyBinaryCondition()],
  };
};

export const createSubscriberTag = (): SubscriberGroup => {
  return {
    type: ADD_TYPE,
    value: '',
  };
};

export const createEmptyBranchConditions = (): BinaryCondition[] => {
  return [createEmptyBinaryCondition()];
};

export const createEmptyBranch = (): ConditionCase => {
  return {
    action_guid: '',
    conditions: createEmptyBranchConditions(),
  };
};

const createElseBranch = (): ConditionCase => {
  return {
    action_guid: '',
    conditions: [],
  };
};

const createEmptyCases = (): ConditionCase[] => {
  return [createEmptyBranch(), createElseBranch()];
};

export const createSendMessageAction = (): SendMessageAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.SEND_MESSAGE,
  params: {
    content: SMS_MESSAGE_INITIAL_VALUE,
    mediaUrl: null,
  },
  next: [],
});

export const createSendEmailAction = (): SendEmailAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.SEND_EMAIL,
  params: {
    body: EMAIL_MESSAGE_INITIAL_VALUE,
    subject: '',
    previewText: null,
    title: null,
  },
  next: [],
});

export const createRcsMessageCard = (): RcsMessageCard => ({
  buttons: [],
  isVideo: false,
  text: RCS_MESSAGE_INITIAL_VALUE,
  thumbnailUrl: null,
  title: '',
  url: RCS_CARD_URL_INITIAL_VALUE,
});

const createRcsTextMessage = (): RcsMessage => ({
  cards: [],
  category: RcsMessageCategories.TEXT,
  content: RCS_MESSAGE_INITIAL_VALUE,
  mediaUrl: null,
  suggestedActions: [],
});

const createRcsCardMessage = (): RcsMessage => ({
  cards: [createRcsMessageCard()],
  category: RcsMessageCategories.CARD,
  content: '',
  mediaUrl: null,
  suggestedActions: [],
});

const createRcsCarouselMessage = (): RcsMessage => ({
  cards: [
    createRcsMessageCard(),
    createRcsMessageCard(),
    createRcsMessageCard(),
  ],
  category: RcsMessageCategories.CAROUSEL,
  content: '',
  mediaUrl: null,
  suggestedActions: [],
});

const createRichMessageFallback = (
  content: string,
  mediaUrl: string | null,
): RichMessageFallback => ({
  guid: uuidv4(),
  content,
  mediaUrl,
});

export const createRcsTextRichMessageActionParams =
  (): RichMessageActionParams => ({
    ...createRcsTextMessage(),
    channel: Channels.RCS,
    fallback: createRichMessageFallback(SMS_MESSAGE_INITIAL_VALUE, null),
  });

export const createRcsCardRichMessageActionParams =
  (): RichMessageActionParams => ({
    ...createRcsCardMessage(),
    channel: Channels.RCS,
    fallback: createRichMessageFallback(
      SMS_MESSAGE_INITIAL_VALUE,
      RCS_CARD_URL_INITIAL_VALUE,
    ),
  });

export const createRcsCarouselRichMessageActionParams =
  (): RichMessageActionParams => ({
    ...createRcsCarouselMessage(),
    channel: Channels.RCS,
    fallback: createRichMessageFallback(
      SMS_MESSAGE_INITIAL_VALUE,
      RCS_CARD_URL_INITIAL_VALUE,
    ),
  });

export const createRichMessageAction = (): RichMessageAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.RICH_MESSAGE,
  params: createRcsTextRichMessageActionParams(),
  frontendConfig: {
    selectingCategory: true,
    selectedPanel: RichMessagePanels.RCS,
  },
  next: [],
});

type PropsType = { withDefaultContent?: boolean };
export const createAIManagedMessageAction = (
  props?: PropsType,
): AIManagedMessageAction => {
  return {
    guid: uuidv4(),
    type: StaticActionsTypes.AI_MANAGED_MESSAGE,
    params: {
      duration: INFINITY_TEST_DEFAULTS.DURATION_HOURS,
      targetMetric: INFINITY_TEST_DEFAULTS.TARGET_METRIC,
      holdbackSize: INFINITY_TEST_DEFAULTS.HOLDBACK_SIZE,
      ...(props?.withDefaultContent && {
        content: SMS_MESSAGE_INITIAL_VALUE,
        mediaUrl: null,
      }),
    },
    next: [],
  };
};

export const createAIManagedOutboundAction = (): AIManagedOutboundAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.AI_MANAGED_OUTBOUND,
  params: {},
  next: [],
});

export const createABSplitCondition = (value: number): Condition => {
  return {
    variable: '_proportional_split',
    operator: '<',
    value,
    type: 'Binary',
    allow_approximate_matches: false,
  };
};

const createABSplitAction = (): ABSplitAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.AB_SPLIT,
  params: {
    cases: [
      {
        // action_guid must be replaced with the first action that follows.
        action_guid: '',
        conditions: [createABSplitCondition(0.5)],
      },
      {
        action_guid: '',
        conditions: [],
      },
    ],
  },
  next: [],
});

export const createOptimizedABSplitAction = (
  flowType: FlowType,
): OptimizedABSplitAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.OPTIMIZED_AB_SPLIT,
  params: {
    cases: [
      {
        // action_guid must be replaced with the first action that follows.
        action_guid: '',
        conditions: [createABSplitCondition(0.5)],
      },
      {
        action_guid: '',
        conditions: [],
      },
    ],
    holdbackRatio: flowType === FlowTypes.CAMPAIGN ? 0.8 : null,
    maxDuration: flowType === FlowTypes.CAMPAIGN ? 'PT6H' : null,
    metric: OptimizationMetricOptions[0].value,
  },
  backendConfig: {
    test_status: OptimizedABSplitTestStatuses.DRAFT,
    status_updated_at: null,
  },
  next: [],
});

const createWaitForEventSplitAction = (): WaitForEventSplitAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.WAIT_FOR_EVENT_SPLIT,
  params: {
    duration: 'PT24H',
    eventType: '',
    cases: createEmptyCases(),
  },
  next: [],
});

const createTriggerEventSplitAction = (): TriggerEventSplitAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.TRIGGER_EVENT_SPLIT,
  params: {
    cases: createEmptyCases(),
  },
  next: [],
});

const createSubscriberAttributeSplitAction =
  (): SubscriberAttributeSplitAction => ({
    guid: uuidv4(),
    type: StaticActionsTypes.SUBSCRIBER_ATTRIBUTE_SPLIT,
    params: {
      cases: createEmptyCases(),
    },
    next: [],
  });

const createUpdateSubscriberAction = (): UpdateSubscriberAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.UPDATE_SUBSCRIBER,
  params: {
    addTags: [],
    removeTags: [],
  },
  next: [],
});

export const createEndAction = (): EndAction => ({
  guid: uuidv4(),
  type: StaticActionsTypes.END,
  params: {},
  next: [],
});

const createDynamicAction = (
  dynamicActionType: DynamicActionType,
): DynamicAction => {
  const params: Record<string, any> = {};

  dynamicActionType.fields.forEach((field) => {
    params[field.options.paramName] = field.options.defaultParamValue;
  });

  return {
    guid: uuidv4(),
    type: dynamicActionType.type,
    params,
    next: [],
  };
};

export interface CreateNewEdgeMeta {
  defaultWaitCategory?: WaitActionCategory;
}

export const createAction = (
  selectedActionType: string,
  flowType: FlowType,
  dynamicActionTypes: DynamicActionType[],
): Action | null => {
  switch (selectedActionType) {
    case StaticActionsTypes.WAIT:
      return createWaitAction();
    case StaticActionsTypes.SEND_MESSAGE:
      return createSendMessageAction();
    case StaticActionsTypes.SEND_EMAIL:
      return createSendEmailAction();
    case StaticActionsTypes.RICH_MESSAGE:
      return createRichMessageAction();
    case StaticActionsTypes.AB_SPLIT:
      return createABSplitAction();
    case StaticActionsTypes.OPTIMIZED_AB_SPLIT:
      return createOptimizedABSplitAction(flowType);
    case StaticActionsTypes.WAIT_FOR_EVENT_SPLIT:
      return createWaitForEventSplitAction();
    case StaticActionsTypes.TRIGGER_EVENT_SPLIT:
      return createTriggerEventSplitAction();
    case StaticActionsTypes.SUBSCRIBER_ATTRIBUTE_SPLIT:
      return createSubscriberAttributeSplitAction();
    case StaticActionsTypes.UPDATE_SUBSCRIBER:
      return createUpdateSubscriberAction();
    case StaticActionsTypes.TEXT_TO_BUY:
      return createTextToBuyAction();
    case StaticActionsTypes.AI_MANAGED_MESSAGE:
      return createAIManagedMessageAction();
    case StaticActionsTypes.AI_MANAGED_OUTBOUND:
      return createAIManagedOutboundAction();
    case StaticActionsTypes.END:
      return createEndAction();
    default: {
      const selectedDynamicActionType = dynamicActionTypes.find(
        ({ type }) => selectedActionType === type,
      );
      if (selectedDynamicActionType)
        return createDynamicAction(selectedDynamicActionType);
      return null;
    }
  }
};

export const createActionCopy = (action: Action): Action => {
  const copy = {
    ...cloneDeep(action),
    guid: uuidv4(),
  };

  // Per FBP-3413: differentiate the duplicate's title from the original.
  // Custom titles get "(copy)" appended (compounds on successive duplicates);
  // auto-numbered titles are cleared so the auto-numbering walk inside
  // addAction assigns the next available number for the type.
  copy.params = {
    ...copy.params,
    customTitle: getDuplicatedActionCustomTitle(
      copy.params?.customTitle,
      copy.type,
    ),
  };

  return copy;
};

export const findActionIndexByGuid = (
  guid: string,
  actions: Action[],
): number | null => {
  const index = actions.findIndex((draftAction) => draftAction.guid === guid);
  return index !== -1 ? index : null;
};

export const findActionByGuid = (
  guid: string,
  actions: Action[],
): Action | null => {
  const index = findActionIndexByGuid(guid, actions);
  return index !== null ? actions[index] : null;
};

export const findActionByNextGuid = (
  guid: string,
  actions: Action[],
): Action | null => {
  const index = actions.findIndex((action) => action.next.includes(guid));
  return index !== -1 ? actions[index] : null;
};

export const replaceNextGuidInAction = (
  oldGuid: string,
  newGuids: string[],
  action: Action,
): void => {
  const index = action.next.indexOf(oldGuid);
  if (index !== -1) action.next.splice(index, 1, ...newGuids);
};

export const mergeActionConfig = (action: Action, config: unknown) => {
  if (
    isRichMessageAction(action) &&
    isRichMessageActionFrontendConfig(config)
  ) {
    return {
      ...action,
      frontendConfig: { ...action.frontendConfig, ...config },
    };
  }

  if (
    isOptimizedABSplitAction(action) &&
    isOptimizedABSplitActionBackendConfig(config)
  ) {
    return {
      ...action,
      backendConfig: { ...action.backendConfig, ...config },
    };
  }

  return action;
};

export const mergeActionParams = (action: Action, params: unknown): Action => {
  if (isWaitAction(action) && isWaitActionParams(params)) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (isSendMessageAction(action) && isSendMessageActionParams(params)) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (isSendEmailAction(action) && isSendEmailActionParams(params)) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (isRichMessageAction(action) && isRichMessageActionParams(params)) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (isABSplitAction(action) && isABSplitActionParams(params)) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (
    isOptimizedABSplitAction(action) &&
    isOptimizedABSplitActionParams(params)
  ) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (
    isUpdateSubscriberAction(action) &&
    isUpdateSubscriberActionParams(params)
  ) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (
    isWaitForEventSplitAction(action) &&
    isWaitForEventSplitActionParams(params)
  ) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (
    isTriggerEventSplitAction(action) &&
    isTriggerEventSplitActionParams(params)
  ) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (
    isSubscriberAttributeSplitAction(action) &&
    isSubscriberAttributeSplitActionParams(params)
  ) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (isTextToBuyAction(action) && isTextToBuyActionParams(params)) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (
    isAIManagedMessageAction(action) &&
    isAIManagedMessageActionParams(params)
  ) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (
    isAIManagedOutboundAction(action) &&
    isAIManagedOutboundActionParams(params)
  ) {
    return { ...action, params: { ...action.params, ...params } };
  }

  if (isDynamicAction(action) && isDynamicActionParams(params)) {
    return { ...action, params: { ...action.params, ...params } };
  }

  return action;
};

export const updateActionNext = (action: Action, next: string[]): Action => {
  const newAction = { ...action, next };

  return newAction;
};

export const textToBuyEventToDrawerType = (
  event: string,
): TextToBuyDrawerType | null => {
  const map: Record<string, TextToBuyDrawerType> = {
    PayOrderConfirmed: TextToBuyDrawerTypes.T2B_PREVIEW_PURCHASED,
    PayOrderCancelled: TextToBuyDrawerTypes.T2B_PREVIEW_CANCELLED,
    PayOrderExpired: TextToBuyDrawerTypes.T2B_PREVIEW_DIDNT_PURCHASE,
  };

  return map[event] || null;
};

export const findSourceActions = (
  guid: string,
  actions: Action[],
): Action[] => {
  return actions.filter((action) => action.next.includes(guid));
};

export const replaceActionGuidInActions = (
  oldGuid: string,
  newGuid: string,
  actions: Action[],
): Action[] => {
  const newActions = cloneDeep(actions);

  for (let i = 0; i < newActions.length; i += 1) {
    const action = newActions[i];

    if (action.guid === oldGuid) action.guid = newGuid;

    for (let j = 0; j < action.next.length; j += 1) {
      if (action.next[j] === oldGuid) action.next[j] = newGuid;
    }

    if (isSplitAction(action)) {
      for (let j = 0; j < action.params.cases.length; j += 1) {
        const branch = action.params.cases[j];
        if (branch.action_guid === oldGuid) branch.action_guid = newGuid;
      }
    }
  }

  return newActions;
};

export const createNewNext = (action: Action, targetGuid: string) => {
  if (isTextToBuyAction(action)) {
    return [targetGuid, targetGuid, targetGuid];
  }

  if (
    isABSplitAction(action) ||
    isOptimizedABSplitAction(action) ||
    isWaitForEventSplitAction(action) ||
    isTriggerEventSplitAction(action) ||
    isSubscriberAttributeSplitAction(action)
  ) {
    return [targetGuid, targetGuid];
  }

  // In the case of a dynamic split action, we need to create as many nexts as cases
  // as there are in the initialCases as defined in the dynamic action definition
  if (isDynamicSplitAction(action)) {
    const dynamicSplitField = getDynamicActionSplitField(action);

    if (dynamicSplitField) {
      return dynamicSplitField.options.defaultParamValue.map(() => targetGuid);
    }
  }

  return [targetGuid];
};

export const isBranchCompleted = (branch: ConditionCase): boolean => {
  return isConditionCase(branch);
};

interface GetMessageActionContentOptions {
  includeFallback?: boolean;
}

export const getMessageActionContent = (
  action:
    | AIManagedMessageAction
    | RichMessageAction
    | SendMessageAction
    | TextToBuyAction,
  options: GetMessageActionContentOptions = {},
): string[] => {
  if (isRichMessageAction(action)) {
    const { content, cards, fallback } = action.params;
    const cardContent =
      cards
        .map((card) => {
          return [card.text, card.title ?? ''];
        })
        .flat() ?? [];

    const contentArray = [content, ...cardContent];

    if (options.includeFallback) contentArray.push(fallback.content);

    return contentArray;
  }

  if (action.params.content) {
    return [action.params.content];
  }

  return [];
};

interface GetCombinedMessageActionContentOptions
  extends GetMessageActionContentOptions {
  separator?: string;
}

export const getCombinedMessageActionContent = (
  action:
    | AIManagedMessageAction
    | RichMessageAction
    | SendMessageAction
    | TextToBuyAction,
  options: GetCombinedMessageActionContentOptions = {},
): string => {
  return getMessageActionContent(action, options).join(
    options.separator ?? '\n\n',
  );
};
